import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatPercent(value: number): string {
  return `${formatNumber(value * 100)}%`;
}

export function formatPricePerShare(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '-';

  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(numericValue)) return '-';

  return `$${formatNumber(numericValue, 2)}`;
}

/**
 * Formats currency values in a compact, human-readable way.
 * Examples:
 * - 2500000 → "$2.5M"
 * - 1800000 → "$1.8M"
 * - 150000 → "$150K"
 * - 1500 → "$1,500"
 */
export function formatCompactCurrency(value: number | string | null | undefined): string {
  if (value == null) return '$0';

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '$0';

  if (numValue >= 1_000_000_000) {
    return `$${(numValue / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  } else if (numValue >= 1_000_000) {
    return `$${(numValue / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  } else if (numValue >= 1_000) {
    return `$${(numValue / 1_000).toFixed(0)}K`;
  } else {
    return `$${formatNumber(numValue, 0)}`;
  }
}

/**
 * Formats large numbers in a user-friendly, rounded way for estimates.
 * Examples:
 * - 4,140,630 → "4M+"
 * - 165,626 → "160K+"
 * - 5,678 → "5K+"
 * - 1,234 → "1,200+"
 * - 234 → "234"
 */
export function formatLargeNumberEstimate(value: number): string {
  if (value >= 1_000_000) {
    // Round down to nearest million
    const millions = Math.floor(value / 1_000_000);
    return `${millions}M+`;
  } else if (value >= 100_000) {
    // Round down to nearest 100K
    const hundredK = Math.floor(value / 100_000) * 100;
    return `${hundredK}K+`;
  } else if (value >= 10_000) {
    // Round down to nearest 10K
    const tenK = Math.floor(value / 10_000) * 10;
    return `${tenK}K+`;
  } else if (value >= 1_000) {
    // Round down to nearest 100
    const hundreds = Math.floor(value / 100) * 100;
    return `${formatNumber(hundreds, 0)}+`;
  } else {
    // Show exact number for small counts
    return formatNumber(value, 0);
  }
}

export const debounce = <A extends unknown[]>(func: (...args: A) => void, wait: number) => {
  let timeout: NodeJS.Timeout;

  return function executedFunction(...args: A) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};
